import { buildSummaryFieldMappingsFromRows } from '../request/fields/projections/summaryRows';
import {
  coolingReadinessSummaryRowValue,
  facilityPowerSummaryRowValue,
  liquidCoolingSummaryRowValue,
  racksSummaryRowValue,
  siteInformationSummaryRowValue,
  smallUpsSummaryRowValue,
} from '../request/fields/projections/summaryRowValues';
import { opportunitySummaryRowValue } from '../request/fields/projections/summaryRowValues';
import {
  getCoolingReadinessSummaryDefinitions,
  getCoolingReadinessSummaryFieldMappings,
  getFacilityPowerSummaryDefinitions,
  getFacilityPowerSummaryFieldMappings,
  getLiquidCoolingSummaryDefinitions,
  getLiquidCoolingSummaryFieldMappings,
  getOpportunitySummaryFieldMappingsFromRegistry,
  getOpportunitySummaryRowsWithRbac,
  getSiteInformationSummaryDefinitions,
  getSiteInformationSummaryFieldMappings,
  getSmallUpsSummaryDefinitions,
  getSmallUpsSummaryFieldMappings,
} from '../request/fields/requestFieldRegistry';
import { getRacksDescriptorSummaryDefinitions } from '../request/sections/racksDescriptorSummary';

import type {
  SummaryCardId,
  SummaryCardSource,
} from './summaryCardSourceTypes';

export { SUMMARY_CARD_IDS } from './summaryCardSourceTypes';
export type {
  SummaryCardFieldMapping,
  SummaryCardId,
  SummaryCardRow,
  SummaryCardSource,
} from './summaryCardSourceTypes';

/**
 * Single registry for macro Summary cards. Add a new card by extending {@link SUMMARY_CARD_IDS}
 * and wiring getters here — `Summary.tsx` should read only this map (no one-off imports per card).
 */
export const SUMMARY_CARD_SOURCES = {
  opportunity: {
    getRowDefinitions: () => [...getOpportunitySummaryRowsWithRbac()],
    getFieldMappings: getOpportunitySummaryFieldMappingsFromRegistry,
    rowValue: opportunitySummaryRowValue,
  },
  siteInformation: {
    getRowDefinitions: getSiteInformationSummaryDefinitions,
    getFieldMappings: getSiteInformationSummaryFieldMappings,
    rowValue: siteInformationSummaryRowValue,
  },
  racks: {
    // #997 P4: macro card shares ONE runtime row-set + field-map with the per-unit
    // rows by sourcing the descriptor engine directly (parity locked by
    // racks-summary-source-parity.test.ts against the retained bespoke oracle).
    getRowDefinitions: getRacksDescriptorSummaryDefinitions,
    getFieldMappings: () =>
      buildSummaryFieldMappingsFromRows(getRacksDescriptorSummaryDefinitions()),
    rowValue: racksSummaryRowValue,
  },
  facilityPower: {
    getRowDefinitions: getFacilityPowerSummaryDefinitions,
    getFieldMappings: getFacilityPowerSummaryFieldMappings,
    rowValue: facilityPowerSummaryRowValue,
  },
  smallUps: {
    getRowDefinitions: getSmallUpsSummaryDefinitions,
    getFieldMappings: getSmallUpsSummaryFieldMappings,
    rowValue: smallUpsSummaryRowValue,
  },
  coolingReadiness: {
    getRowDefinitions: getCoolingReadinessSummaryDefinitions,
    getFieldMappings: getCoolingReadinessSummaryFieldMappings,
    rowValue: coolingReadinessSummaryRowValue,
  },
  liquidCooling: {
    getRowDefinitions: getLiquidCoolingSummaryDefinitions,
    getFieldMappings: getLiquidCoolingSummaryFieldMappings,
    rowValue: liquidCoolingSummaryRowValue,
  },
} satisfies Record<SummaryCardId, SummaryCardSource>;

export function getSummaryCardSource(id: SummaryCardId): SummaryCardSource {
  switch (id) {
    case 'opportunity':
      return SUMMARY_CARD_SOURCES.opportunity;
    case 'siteInformation':
      return SUMMARY_CARD_SOURCES.siteInformation;
    case 'racks':
      return SUMMARY_CARD_SOURCES.racks;
    case 'facilityPower':
      return SUMMARY_CARD_SOURCES.facilityPower;
    case 'smallUps':
      return SUMMARY_CARD_SOURCES.smallUps;
    case 'coolingReadiness':
      return SUMMARY_CARD_SOURCES.coolingReadiness;
    case 'liquidCooling':
      return SUMMARY_CARD_SOURCES.liquidCooling;
    default: {
      const _exhaustive: never = id;
      return _exhaustive;
    }
  }
}
